/**
 * Traduz mensagens de sistema mantendo valores dinâmicos (nomes, datas, horas)
 * 
 * Esta função é aplicada apenas em mensagens de sistema renderizadas pelo componente SystemMessage.
 * Mensagens de sistema não têm o atributo data-origin, enquanto mensagens normais têm
 * data-origin=sent ou data-origin=received.
 * 
 * @param {string} text - Texto da mensagem de sistema
 * @param {Function} translateFn - Função de tradução (useGlotaT ou similar)
 * @param {Object} options - Opções adicionais (dtm, timezone, etc)
 * @returns {string} - Texto traduzido ou original se não corresponder a nenhum padrão de sistema
 */
import { format } from 'date-fns';

export function translateSystemMessage(text, translateFn, options = {}) {
  if (!text || typeof text !== 'string') return text;

  const { dtm, timezone } = options;
  
  // Função helper para formatar data se disponível
  const formatDate = (dtmValue) => {
    if (!dtmValue) return null;
    try {
      return format(new Date(Number(dtmValue * 1000)), 'dd/MM/yyyy');
    } catch (e) {
      return null;
    }
  };

  // Padrão: "Contato redirecionado para {nome} por {nome} em {data} às {hora}"
  // Exemplo: "Contato redirecionado para Ítalo Alves por Nikole em 30/10/2025 às 11:48"
  const redirectPattern = /^Contato redirecionado para (.+?) por (.+?) em (.+?) às (.+?)$/;
  if (redirectPattern.test(text)) {
    const match = text.match(redirectPattern);
    const toName = match[1];
    const byName = match[2];
    const date = match[3];
    const time = match[4];
    const template = translateFn('spa.chat.systemMessages.contactRedirected');
    return template
      .replace(':toName', toName)
      .replace(':byName', byName)
      .replace(':date', date)
      .replace(':time', time);
  }

  // Padrão: "Contato redirecionado para operador pelo sistema em {data} às {hora}"
  // Exemplo: "Contato redirecionado para operador pelo sistema em 30/06/2025 às 16:16"
  const redirectSystemPattern = /^Contato redirecionado para operador pelo sistema em (.+?) às (.+?)$/;
  if (redirectSystemPattern.test(text)) {
    const match = text.match(redirectSystemPattern);
    const date = match[1];
    const time = match[2];
    const template = translateFn('spa.chat.systemMessages.contactRedirectedBySystem');
    return template.replace(':date', date).replace(':time', time);
  }

  // Padrão: "Este contato foi editado em {data}" (PT) ou "Este contacto fue editado el {data}" (ES)
  // Exemplo: "Este contato foi editado em 31/10/2025"
  // Também detecta se já contém ":date" sem substituir
  const editedPatternPT = /^Este contato foi editado em (.+?)$/;
  const editedPatternES = /^Este contacto fue editado el (.+?)$/;
  
  // Verifica se a mensagem já contém ":date" (significa que foi traduzida mas não substituída)
  // Isso pode acontecer quando a tradução é aplicada mas o parâmetro não é substituído
  if (text.includes(':date')) {
    let dateToUse = null;
    
    // Primeiro tenta extrair a data do texto usando padrões mais flexíveis
    const dateMatch = text.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
    if (dateMatch) {
      dateToUse = dateMatch[1];
    } else if (dtm) {
      // Se não encontrou data no texto mas tem dtm disponível, usa ele
      dateToUse = formatDate(dtm);
    }
    
    if (dateToUse) {
      const template = translateFn('spa.chat.systemMessages.contactEdited');
      return template.replace(':date', dateToUse);
    }
    
    // Se não encontrou data em nenhum lugar, retorna o template sem o :date
    // (melhor que mostrar :date literalmente)
    const template = translateFn('spa.chat.systemMessages.contactEdited');
    return template.replace(':date', '').trim();
  }
  
  if (editedPatternPT.test(text)) {
    const match = text.match(editedPatternPT);
    const date = match[1];
    const template = translateFn('spa.chat.systemMessages.contactEdited');
    return template.replace(':date', date);
  }
  
  if (editedPatternES.test(text)) {
    const match = text.match(editedPatternES);
    const date = match[1];
    const template = translateFn('spa.chat.systemMessages.contactEdited');
    return template.replace(':date', date);
  }

  // Padrão: "Esta conversa foi finalizada por {nome} em {data} às {hora}"
  // Exemplo: "Esta conversa foi finalizada por Nikole em 31/10/2025 às 09:44"
  const finishedPattern = /^Esta conversa foi finalizada por (.+?) em (.+?) às (.+?)$/;
  if (finishedPattern.test(text)) {
    const match = text.match(finishedPattern);
    const byName = match[1];
    const date = match[2];
    const time = match[3];
    const template = translateFn('spa.chat.systemMessages.conversationFinished');
    return template
      .replace(':byName', byName)
      .replace(':date', date)
      .replace(':time', time);
  }

  // Se não corresponder a nenhum padrão de mensagem de sistema conhecido,
  // retorna o texto original (não traduz mensagens normais)
  return text;
}

export default translateSystemMessage;

